import { useState, useCallback } from 'react';
import { fireErrorAnalytics } from '@atlassian/jira-errors-handling';
import {
	APP_TEAM_NAME,
	useExperienceTracker,
} from '@atlassian/jira-project-create-importer-common';
import { APP_IDENTIFIER } from '../../common/constants';
import { EXPERIENCES, validateMappingInteractionMetric } from '../../common/constants/metrics';
import type { Source } from '../../common/constants/source';
import type { X2CImportValidationError } from '../../common/types';
import { useAnalyticsAttributesContext } from '../../common/utils/analtics-attributes-context';
import { useGetCSVImportClient } from '../../common/utils/csv-import-clients';
import type { UseValidateX2CImport, ValidateX2CImportArgs } from './types';

export const useValidateX2CImport = (
	source: Source,
	serviceName?: string,
): UseValidateX2CImport => {
	const [onExperienceStart, onExperienceSuccess, onExperienceFail] = useExperienceTracker(
		EXPERIENCES.VALIDATE,
	);
	const [isLoading, setIsLoading] = useState(false);
	const additionalAttributes = useAnalyticsAttributesContext();
	const client = useGetCSVImportClient(source, serviceName);

	const validateX2CImport = useCallback(
		async ({
			migrationId,
			mappingState,
			advancedOptions,
			onSuccess,
			onFetchFail,
			onValidationError,
		}: ValidateX2CImportArgs) => {
			setIsLoading(true);
			let validationResponse: X2CImportValidationError;

			validateMappingInteractionMetric.start();
			onExperienceStart();

			try {
				validationResponse = await client.validateRequest({
					migrationId,
					mappingState,
					advancedOptions,
				});
				validateMappingInteractionMetric.stop();
				onExperienceSuccess({ migrationId, ...additionalAttributes });
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
			} catch (error: any) {
				validateMappingInteractionMetric.stop();
				onExperienceFail(APP_IDENTIFIER, error, { migrationId, ...additionalAttributes });
				fireErrorAnalytics({
					meta: {
						id: 'validate-x2c-import-error',
						packageName: APP_IDENTIFIER,
						teamName: APP_TEAM_NAME,
					},
					attributes: {
						message: 'Failed to validate payload for jwm import',
					},
					error,
					sendToPrivacyUnsafeSplunk: true,
				});
				setIsLoading(false);
				onFetchFail();
				return;
			}
			setIsLoading(false);
			if (
				Object.keys(validationResponse?.columnErrors ?? {}).length > 0 ||
				validationResponse?.missingFields?.length > 0
			) {
				onValidationError(validationResponse);
				return;
			}
			onSuccess();
		},
		[onExperienceStart, onExperienceSuccess, additionalAttributes, client, onExperienceFail],
	);

	return {
		isLoading,
		validateX2CImport,
	};
};
